import React from 'react';
import { Box, BoxProps, FinalRateProps, FlexDirection, JustifyContent, Typography } from '@drone-js/core';
import { Spacing } from '@kite-js/theme';
import { TypographyProps } from '@kite-js/experimental/lib/Typography';
import { CurrencyDisplay, PriceFormatter, StringFormatter } from '@agoda/react-handyman';
import { useSelector } from 'react-redux';
import { selectConfigFromBootstrap, selectLocaleFromBootstrap } from 'store/slice/bootstrap';
import { PriceWithPrefix } from 'component/common/Price/PriceWithPrefix';
import { TextType, TypographyType } from '@drone-js/theme';
import { PointsBehavior, PriceBehavior } from 'core/type/behaviors.types';
import { AlignItems } from '@drone-js/core/src/types/Types';

const priceAmountWithPointsBoxPropsBuilder = (isMobile: boolean, isPointAtFirst: boolean): BoxProps => {
    return isMobile && !isPointAtFirst
        ? {
              flexDirection: 'column',
              gap: '0',
              alignItems: 'end',
              justifyContent: 'start',
          }
        : {
              flexDirection: 'row',
              gap: '4',
              alignItems: 'center',
              justifyContent: 'end',
          };
};

interface PriceWrapperProps {
    activityId?: number;
    amount: number;
    corAmount?: number;
    decimal?: number;
    currency: string;
    currencyDisplay?: CurrencyDisplay;
    // styling
    overrideAmountProps?: TypographyProps;
    spacing?: Spacing;
    // additional options
    prefix?: string;
    isSpaceBetweenSymbolAndCurrency?: boolean;
    payablePrice?: number;
    priceDirection?: FlexDirection;
    priceJustifyContent?: JustifyContent;
    finalPriceSize?: FinalRateProps['size'];
    priceAlignItems?: AlignItems;
}

export const PriceAmount: React.FC<PriceWrapperProps> = React.memo((props) => {
    const locale = useSelector(selectLocaleFromBootstrap);

    const decimal = (props.decimal || props.decimal === 0) && props.decimal >= 0 ? props.decimal : 2;
    const isSpaceBetweenSymbolAndCurrency = props.isSpaceBetweenSymbolAndCurrency === undefined ? true : props.isSpaceBetweenSymbolAndCurrency;
    const formattedCorAmount =
        props.corAmount && props.corAmount !== 0
            ? PriceFormatter.format(props.corAmount, locale, {
                  isSpaceBetweenSymbolAndCurrency,
                  currency: props.currency,
                  currencyDisplay: props.currencyDisplay || CurrencyDisplay.Symbol,
                  decimalPlaces: decimal,
              })
            : null;
    const formattedAmountTokens = PriceFormatter.formatToParts(props.amount, locale, {
        isSpaceBetweenSymbolAndCurrency,
        currency: props.currency,
        currencyDisplay: props.currencyDisplay || CurrencyDisplay.Symbol,
        decimalPlaces: decimal,
    });

    return (
        <PriceWithPrefix
            formattedAmountTokens={formattedAmountTokens}
            formattedCorAmount={formattedCorAmount || undefined}
            prefix={props.prefix}
            priceDirection={props.priceDirection}
            priceJustifyContent={props.priceJustifyContent}
            finalPriceSize={props.finalPriceSize}
            priceAlignItems={props.priceAlignItems}
        />
    );
});
PriceAmount.displayName = 'PriceAmount';

interface PriceAmountWithPointsProps extends Pick<PriceWrapperProps, 'amount' | 'corAmount' | 'currency' | 'overrideAmountProps' | 'prefix' | 'payablePrice' | 'finalPriceSize'> {
    formattedPoints: string;
    formattedPayablePrice: string;
    decimal?: number;
    priceDirection?: FlexDirection;
    priceJustifyContent?: JustifyContent;
}

export const PriceAmountWithPoints: React.FC<PriceAmountWithPointsProps> = (props) => {
    const config = useSelector(selectConfigFromBootstrap);
    const burn = config.behavior.common.burn;
    const pointsText =
        props.payablePrice && props.payablePrice > 0
            ? StringFormatter.format(config.cms[burn.pointsWithExtraCms || 155794], props.formattedPoints, props.formattedPayablePrice)
            : StringFormatter.format(config.cms[burn.pointsCms], props.formattedPoints);

    return (
        <Box display="flex" flexDirection="column" ms="0" flexWrap="wrap" data-testid="activities-price-amount-with-points" data-price-type="activities-price-amount-with-points">
            <PriceAmount
                amount={props.amount}
                corAmount={props.corAmount}
                currency={props.currency}
                overrideAmountProps={props.overrideAmountProps}
                prefix={props.prefix}
                decimal={props.decimal}
                priceDirection={props.priceDirection}
                priceJustifyContent={props.priceJustifyContent}
                finalPriceSize={props.finalPriceSize}
            />
            <div data-element-name="activities-points">
                <Typography variant="body-s-strong" textColor={burn.pointsColor as TextType}>
                    {pointsText}
                </Typography>
            </div>
        </Box>
    );
};
PriceAmountWithPoints.displayName = 'PriceAmountWithPoints';

interface PriceAmountWithPointsOnSameLineProps
    extends Pick<PriceWrapperProps, 'amount' | 'corAmount' | 'currency' | 'overrideAmountProps' | 'prefix' | 'payablePrice' | 'finalPriceSize'> {
    formattedPoints: string;
    formattedPayablePrice: string;
    decimal?: number;
    priceDirection?: FlexDirection;
    priceJustifyContent?: JustifyContent;
    isMobileScreen?: boolean;
}

const renderPoints = (config: PointsBehavior, pointsText: React.ReactNode[]) => {
    return (
        <div data-element-name="activities-points">
            <Typography display="flex" variant={config.pointsTextVariant as TypographyType} textColor={config.pointsTextColor as TextType}>
                <Box display="flex" alignItems="end" gap="4">
                    {pointsText}
                </Box>
            </Typography>
        </div>
    );
};

const renderPriceWithCurrency = (config: PriceBehavior, currency: string, formattedAmount: string) => {
    return (
        <Box display="flex" gap="4" alignItems="end" data-testid="activities-price">
            <Typography variant={config.currencyVariant as TypographyType} textColor={config.currencyTextColor as TextType}>
                {currency}
            </Typography>
            <Typography variant={config.amountVariant as TypographyType} textColor={config.amountTextColor as TextType}>
                {formattedAmount}
            </Typography>
        </Box>
    );
};

const getTypographyVariantAndColor = (priceConfig: PriceBehavior, pointConfig: PointsBehavior) => {
    if (!priceConfig.isPointAtFirst) {
        return { variant: pointConfig.pointsVariant as TypographyType, textColor: pointConfig.pointsTextColor as TextType };
    }

    return { variant: priceConfig.amountVariant as TypographyType, textColor: priceConfig.amountTextColor as TextType };
};

export const PriceAmountWithPointsOnSameLine: React.FC<PriceAmountWithPointsOnSameLineProps> = (props) => {
    const config = useSelector(selectConfigFromBootstrap);
    const priceAndPointCms = config.cms[134330];
    const pointsCms = config.cms[319560];
    const pointConfig = config.behavior.common.burn;
    const priceConfig = config.behavior.common.price;
    const pointsText = StringFormatter.formatToJsx(
        pointsCms,
        <Typography textColor={pointConfig.pointsColor as TextType} variant={pointConfig.pointsVariant as TypographyType}>
            {props.formattedPoints}
        </Typography>,
    );
    const locale = useSelector(selectLocaleFromBootstrap);
    const decimal = (props.decimal || props.decimal === 0) && props.decimal >= 0 ? props.decimal : 2;
    const formattedAmount = PriceFormatter.format(props.amount, locale, {
        decimalPlaces: decimal,
    });

    return (
        <Box
            display="flex"
            ms="0"
            flexWrap="wrap"
            data-element-name="activities-price-amount-with-points"
            data-price-type="activities-price-amount-with-points"
            {...priceAmountWithPointsBoxPropsBuilder(!!props.isMobileScreen, priceConfig.isPointAtFirst)}
        >
            <Typography display="flex" {...getTypographyVariantAndColor(priceConfig, pointConfig)}>
                <Box display="flex" alignItems="baseline" gap="4">
                    {StringFormatter.formatToJsx(
                        priceAndPointCms,
                        priceConfig.isPointAtFirst ? renderPoints(pointConfig, pointsText) : renderPriceWithCurrency(priceConfig, props.currency, formattedAmount),
                        !priceConfig.isPointAtFirst ? renderPoints(pointConfig, pointsText) : renderPriceWithCurrency(priceConfig, props.currency, formattedAmount),
                    )}
                </Box>
            </Typography>
        </Box>
    );
};
PriceAmountWithPointsOnSameLine.displayName = 'PriceAmountWithPointsOnSameLine';
